import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../../components/Admin/Layout/AdminLayout';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
import {
  PieChart,
  TrendingUp,
  DollarSign,
  Users,
  Clock,
  FileText,
  CreditCard,
  Receipt,
  Utensils,
  Calendar,
  BarChart3,
  Star,
  ConciergeBell,
  Package,
  Percent,
  Search,
  X,
  Pin,
  Zap
} from 'lucide-react';

/**
 * Design V3: Tab-based Compact Layout (Improved)
 *
 * Features:
 * - Horizontal tabs for category navigation
 * - Search with clear button
 * - Compact grid layout (better space utilization)
 * - Quick Access pinned reports
 * - Clean, professional design
 */

// Report categories configuration
const reportCategories = [
  {
    id: 'all',
    name: 'All Reports',
    icon: BarChart3,
    color: 'gray'
  },
  {
    id: 'sales',
    name: 'Sales',
    icon: TrendingUp,
    color: 'teal',
    reports: [
      { id: 'daily-sales', name: 'Daily Sales Summary', description: 'Daily revenue with payment modes and order counts', path: '/admin/reports/daily-sales', icon: Calendar, pinned: true },
      { id: 'item-wise-sales', name: 'Item-wise Sales', description: 'Best & worst selling items, revenue per item', path: '/admin/reports/item-wise-sales', icon: Utensils, pinned: true },
      { id: 'category-sales', name: 'Category-wise Sales', description: 'Sales breakdown by menu category', path: '/admin/reports/category-sales', icon: PieChart },
      { id: 'hourly-sales', name: 'Hourly Sales Heatmap', description: 'Peak hours analysis and patterns', path: '/admin/reports/hourly-sales', icon: Clock, pinned: true }
    ]
  },
  {
    id: 'financial',
    name: 'Financial',
    icon: DollarSign,
    color: 'emerald',
    reports: [
      { id: 'payment-analysis', name: 'Payment Analysis', description: 'Cash, UPI, card breakdown with trends', path: '/admin/reports/payment-analysis', icon: CreditCard, pinned: true },
      { id: 'gst-report', name: 'GST Report', description: 'Tax-compliant summary for filing', path: '/admin/reports/gst-report', icon: Receipt, pinned: true },
      { id: 'discount-analysis', name: 'Discount Analysis', description: 'Discount impact on revenue', path: '/admin/reports/discount-analysis', icon: Percent },
      { id: 'revenue-summary', name: 'Revenue Summary', description: 'Weekly/monthly trends', path: '/admin/reports/revenue-summary', icon: BarChart3 }
    ]
  },
  {
    id: 'customer',
    name: 'Customers',
    icon: Users,
    color: 'blue',
    reports: [
      { id: 'customer-insights', name: 'Customer Insights', description: 'Top customers, visit frequency', path: '/admin/reports/customer-insights', icon: Star },
      { id: 'loyalty-report', name: 'Loyalty Points', description: 'Points earned & redeemed', path: '/admin/reports/loyalty-report', icon: Star },
      { id: 'new-vs-repeat', name: 'New vs Repeat', description: 'Retention metrics', path: '/admin/reports/new-vs-repeat', icon: Users }
    ]
  },
  {
    id: 'operational',
    name: 'Operations',
    icon: ConciergeBell,
    color: 'purple',
    reports: [
      { id: 'waiter-performance', name: 'Waiter Performance', description: 'Sales per waiter, table turns', path: '/admin/reports/waiter-performance', icon: ConciergeBell },
      { id: 'table-utilization', name: 'Table Utilization', description: 'Turnover rate, occupancy', path: '/admin/reports/table-utilization', icon: Clock },
      { id: 'kot-timing', name: 'KOT Timing', description: 'Kitchen prep times', path: '/admin/reports/kot-timing', icon: Clock }
    ]
  },
  {
    id: 'inventory',
    name: 'Inventory',
    icon: Package,
    color: 'amber',
    reports: [
      { id: 'stock-status', name: 'Stock Status', description: 'Current levels & alerts', path: '/admin/reports/stock-status', icon: Package },
      { id: 'consumption-report', name: 'Consumption Report', description: 'Usage & wastage tracking', path: '/admin/reports/consumption-report', icon: FileText }
    ]
  }
];

// Color schemes
const colorSchemes = {
  gray: { tab: 'border-gray-600 text-gray-600', bg: 'bg-gray-50', text: 'text-gray-600', icon: 'bg-gray-100', border: 'border-gray-200' },
  teal: { tab: 'border-teal-600 text-teal-600', bg: 'bg-teal-50', text: 'text-teal-600', icon: 'bg-teal-100', border: 'border-teal-200' },
  emerald: { tab: 'border-emerald-600 text-emerald-600', bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'bg-emerald-100', border: 'border-emerald-200' },
  blue: { tab: 'border-blue-600 text-blue-600', bg: 'bg-blue-50', text: 'text-blue-600', icon: 'bg-blue-100', border: 'border-blue-200' },
  purple: { tab: 'border-purple-600 text-purple-600', bg: 'bg-purple-50', text: 'text-purple-600', icon: 'bg-purple-100', border: 'border-purple-200' },
  amber: { tab: 'border-amber-600 text-amber-600', bg: 'bg-amber-50', text: 'text-amber-600', icon: 'bg-amber-100', border: 'border-amber-200' }
};

// Get all reports with category info
const getAllReports = (hasPermission) => {
  return reportCategories
    .filter(cat => cat.reports)
    .flatMap(cat =>
      cat.reports
        .filter(r => {
          if (r.id === "daily-sales") return hasPermission(P.REPORTS.SALES.DAILY_EXPORT);
          if (r.id === "item-wise-sales") return hasPermission(P.REPORTS.SALES.ITEMWISE_EXPORT);
          if (r.id === "category-sales") return hasPermission(P.REPORTS.SALES.CATEGORYWISE_EXPORT);
          if (r.id === "hourly-sales") return hasPermission(P.REPORTS.SALES.HOURLY_EXPORT);

          if (r.id === "payment-analysis") return hasPermission(P.REPORTS.FINANCIAL.PAYMENT_EXPORT);
          if (r.id === "gst-report") return hasPermission(P.REPORTS.FINANCIAL.GST_DOWNLOAD);
          if (r.id === "discount-analysis") return hasPermission(P.REPORTS.FINANCIAL.DISCOUNT_EXPORT);
          if (r.id === "revenue-summary") return hasPermission(P.REPORTS.FINANCIAL.REVENUE_EXPORT);

          if (r.id === "customer-insights") return hasPermission(P.REPORTS.CUSTOMERS.INSIGHTS_EXPORT);
          if (r.id === "loyalty-report") return hasPermission(P.REPORTS.CUSTOMERS.LOYALTY_EXPORT);
          if (r.id === "new-vs-repeat") return hasPermission(P.REPORTS.CUSTOMERS.NEWREPEAT_EXPORT);

          if (r.id === "waiter-performance") return hasPermission(P.REPORTS.OPERATIONS.WAITER_EXPORT);
          if (r.id === "table-utilization") return hasPermission(P.REPORTS.OPERATIONS.TABLE_EXPORT);
          if (r.id === "kot-timing") return hasPermission(P.REPORTS.OPERATIONS.KOT_EXPORT);

          if (r.id === "stock-status") return hasPermission(P.REPORTS.INVENTORY.STOCK_EXPORT);
          if (r.id === "consumption-report") return hasPermission(P.REPORTS.INVENTORY.CONSUMPTION_EXPORT);

          return true;
        })
        .map(r => ({
          ...r,
          category: cat.name,
          categoryId: cat.id,
          categoryColor: cat.color
        }))
    );
};

export default function ReportsHubPageV3() {
  const { hasPermission } = useAuth();
  const canAccessReports = hasPermission(P.REPORTS.ACCESS);
  if (!canAccessReports) {
  return (
    <AdminLayout>
      <div className="p-6 text-center">
        <p className="text-red-500 font-semibold">
          You do not have permission to access Reports
        </p>
      </div>
    </AdminLayout>
  );
}
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from URL or default to 'all'
  const initialTab = searchParams.get('tab') || 'all';
  const validTabs = reportCategories.map(c => c.id);
  const [activeTab, setActiveTab] = useState(validTabs.includes(initialTab) ? initialTab : 'all');
  const [searchQuery, setSearchQuery] = useState('');

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchQuery('');
    if (tabId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ tab: tabId });
    }
  };

  const allReports = getAllReports(hasPermission);
  const pinnedReports = allReports.filter(r => r.pinned);

  // Get reports for current tab
  const getDisplayReports = () => {
    let reports = activeTab === 'all'
      ? allReports
      : reportCategories.find(c => c.id === activeTab)?.reports || [];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      reports = allReports.filter(r =>
        r.name.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query) ||
        r.category.toLowerCase().includes(query)
      );
    }

    return reports;
  };

  const displayReports = getDisplayReports();
  const activeCategory = reportCategories.find(c => c.id === activeTab);
  const activeColor = colorSchemes[activeCategory?.color || 'gray'];

  const handleReportClick = (path) => {
    navigate(path);
  };

  return (
    <AdminLayout>
      <div className="min-h-full bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 pt-5 pb-0">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {allReports.length} reports across {reportCategories.length - 1} categories
                </p>
              </div>

              {/* Search */}
              <div className="relative w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 -mb-px overflow-x-auto">
              {reportCategories.map(category => {
                const Icon = category.icon;
                const isActive = activeTab === category.id;
                const colors = colorSchemes[category.color];
                const reportCount = category.reports?.length || allReports.length;

                return (
                  <button
                    key={category.id}
                    onClick={() => handleTabChange(category.id)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
                      isActive
                        ? `${colors.tab} border-b-2 bg-white`
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="font-medium text-sm">{category.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${isActive ? colors.bg : 'bg-gray-100'}`}>
                      {reportCount}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Quick Access - Only show on "All" tab and no search */}
          {activeTab === 'all' && !searchQuery && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <Zap size={14} className="text-amber-500" />
                <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Quick Access</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {pinnedReports.map(report => {
                  const Icon = report.icon;
                  const colors = colorSchemes[report.categoryColor];
                  return (
                    <button
                      key={report.id}
                      onClick={() => handleReportClick(report.path)}
                      className={`${colors.bg} border ${colors.border} rounded-md px-2.5 py-2 text-left hover:shadow-md hover:scale-[1.02] hover:border-teal-400 active:scale-[0.98] transition-all duration-150 flex items-center gap-2 group`}
                    >
                      <Icon size={14} className={`${colors.text} group-hover:text-teal-600 transition-colors`} />
                      <span className="font-medium text-gray-800 text-xs truncate group-hover:text-teal-700 transition-colors">{report.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search Results Header */}
          {searchQuery && (
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium">{displayReports.length}</span> results for "<span className="font-medium">{searchQuery}</span>"
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
              >
                Clear search
              </button>
            </div>
          )}

          {/* Reports Grid */}
          {displayReports.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {displayReports.map(report => {
                const Icon = report.icon;
                const reportColor = colorSchemes[report.categoryColor || activeCategory?.color || 'gray'];

                return (
                  <button
                    key={report.id}
                    onClick={() => handleReportClick(report.path)}
                    className={`bg-white rounded-lg border ${reportColor.border} p-3 text-left hover:shadow-md hover:border-teal-400 transition-all group`}
                  >
                    {/* Header - Icon + Title + Category in same row */}
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-7 h-7 ${reportColor.icon} rounded-md flex items-center justify-center flex-shrink-0`}>
                        <Icon size={15} className={reportColor.text} />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm truncate">{report.name}</h3>
                      {(activeTab === 'all' || searchQuery) && (
                        <span className={`text-[10px] px-1.5 py-px ${reportColor.bg} ${reportColor.text} rounded font-medium flex-shrink-0`}>
                          {report.category}
                        </span>
                      )}
                      {report.pinned && (
                        <Pin size={12} className="text-amber-500 flex-shrink-0 ml-auto" />
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-xs text-gray-500 line-clamp-2 ml-9">{report.description}</p>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Search size={40} className="mx-auto text-gray-300 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">No reports found</h3>
              <p className="text-sm text-gray-500 mb-4">Try adjusting your search</p>
              <button
                onClick={() => setSearchQuery('')}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
              >
                Clear Search
              </button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
